import { Response } from "node-fetch";
import * as foundationUtils from "@foundation/util";

import { stringifyParams } from "../../util/stringifyParams";
import Singleton from "../Singleton";

import { PersonalizationResourceType } from "./enums";
import { PersonalizationClientError } from "./Error";
import {
  type PersonalizationResource,
  type PersonalizationCFRUParams,
  type PersonalizationClientParams,
  type PersonalizationErrorResponse,
  type PersonalizationPopularParams,
  type PersonalizationRecentParams,
  type PersonalizationRecommendedPopularParams,
  type PersonalizationRelatedParams,
  type PersonalizationResourceTypeTags,
  PersonalizationRecommendedTPRParams,
} from "./types";

const { fetchWithProxy } = foundationUtils;

export default class PersonalizationClient extends Singleton {
  host: string;
  environment: string;
  fetchWithProxy: (url: string, method: "GET" | "POST" | "PUT" | "DELETE", proxy?: string) => Promise<Response>;

  constructor(params: PersonalizationClientParams) {
    super();

    if (!params.host) {
      throw new Error(
        `No host passed to PersonalizationClient constructor; aborting`
      );
    }

    if (!params.environment) {
      throw new Error(`No environment passed to PersonalizationClient constructor; aborting`);
    }

    this.host = params.host;
    this.environment = params.environment;
    this.fetchWithProxy = (url, method, proxy) => {
      const stagingEnvsWithProdPersonalization = new Set(["orange"]);
      const isStagingEnvWithProdPersonalization = stagingEnvsWithProdPersonalization.has(this.environment);

      if (isStagingEnvWithProdPersonalization && method !== "GET") {
       throw new Error(`Only GET request method is allowed from PersonalizationClient in ${Array.from(stagingEnvsWithProdPersonalization).join(',')}. It is trying to be called from: ${this.environment}`);
      }

      return fetchWithProxy(url, proxy, {
	      method
      });
    }
  }

  async getCFRU<T extends PersonalizationResource>(
    params?: PersonalizationCFRUParams,
    proxy?: string
  ): Promise<T[]> {
    this.validateGetCFRU(params);

    const urlParams = stringifyParams(params, (arr) => arr.join(";"));
    const url = `${this.host}/user/recommendations/cfru?${urlParams}`;

    const response = await this.fetchWithProxy(url, "GET", proxy);
    const data = await response.json();

    if (!response.ok) {
      throw new PersonalizationClientError({
        errorResponse: data as PersonalizationErrorResponse,
        status: response.status,
        url,
      });
    }

    return data;
  }

  async getRecent<T extends PersonalizationResource>(
    params: PersonalizationRecentParams,
    proxy?: string
  ): Promise<T[]> {
    this.validateGetRecent(params);

    const urlParams = stringifyParams(params, (arr) => arr.join(";"));
    const url = `${this.host}/user/recommendations/recent?${urlParams}`;

    const response = await this.fetchWithProxy(url, "GET", proxy);
    const data = await response.json();

    if (!response.ok) {
      throw new PersonalizationClientError({
        errorResponse: data as PersonalizationErrorResponse,
        status: response.status,
        url,
      });
    }

    return data;
  }

  async getRelated<T extends PersonalizationResource>(
    params: PersonalizationRelatedParams,
    proxy?: string
  ): Promise<T[]> {
    this.validateGetRelated(params);

    const urlParams = stringifyParams(params, (arr) => arr.join(";"));
    const url = `${this.host}/user/recommendations/rr?${urlParams}`;

    const response = await this.fetchWithProxy(url, "GET", proxy);
    const data = await response.json();

    if (!response.ok) {
      throw new PersonalizationClientError({
        errorResponse: data as PersonalizationErrorResponse,
        status: response.status,
        url,
      });
    }

    return data;
  }

  async getPopularResources<T extends PersonalizationResource>(
    params?: PersonalizationPopularParams,
    proxy?: string
  ): Promise<T[]> {
    this.validateGetPopular(params);

    const urlParams = stringifyParams(params, (arr) => arr.join(";"));
    const url = `${this.host}/popular/resources?${urlParams}`;

    const response = await this.fetchWithProxy(url, "GET", proxy);
    const data = await response.json();

    if (!response.ok) {
      throw new PersonalizationClientError({
        errorResponse: data as PersonalizationErrorResponse,
        status: response.status,
        url,
      });
    }

    const facet = Object.keys(data)[0];

    return data[facet];
  }

  async getPopularEntities<T extends PersonalizationResourceTypeTags>(
    params?: PersonalizationPopularParams, 
    proxy?: string
  ): Promise<T[]> {
    this.validateGetPopular(params);

    const urlParams = stringifyParams(params, (arr) => arr.join(";"));
    const url = `${this.host}/popular/entities?${urlParams}`;

    const response = await this.fetchWithProxy(url, "GET", proxy);
    const data = await response.json();

    if (!response.ok) {
      throw new PersonalizationClientError({
        errorResponse: data as PersonalizationErrorResponse,
        status: response.status,
        url,
      });
    }

    return data;
  }

  async getRecommendedPopular<T extends PersonalizationResource>(
    params?: PersonalizationRecommendedPopularParams,
    proxy?: string
  ): Promise<T[]> {
    this.validateGetRecommendedPopular(params);

    const urlParams = stringifyParams(params, (arr) => arr.join(";"));
    const url = `${this.host}/user/recommendations/popular?${urlParams}`;

    const response = await this.fetchWithProxy(url, "GET", proxy);
    const data = await response.json();

    if (!response.ok) {
      throw new PersonalizationClientError({
        errorResponse: data as PersonalizationErrorResponse,
        status: response.status,
        url,
      });
    }

    return data;
  }

  async getRecommendedTPR<T extends PersonalizationResource>(
    params: PersonalizationRecommendedTPRParams,
    proxy?: string
  ): Promise<T[]> {
    this.validateGetRecommendedTPR(params);

    const urlParams = stringifyParams(params, (arr) => arr.join(";"));
    const url = `${this.host}/user/recommendations/tpr?${urlParams}`;

    const response = await this.fetchWithProxy(url, "GET", proxy);
    const data = await response.json();

    if (!response.ok) {
      throw new PersonalizationClientError({
        errorResponse: data as PersonalizationErrorResponse,
        status: response.status,
        url,
      });
    }

    return data;
  }

  validateGetCFRU = (params?: PersonalizationCFRUParams): boolean => {
    if (!params) {
      return true;
    }

    if (
      !params.resourceTypes ||
      !Array.isArray(params.resourceTypes) ||
      params.resourceTypes.length === 0
    ) {
      throw new Error(
        "Missing required parameter for PersonalizationClient.getCFRU: resourceTypes"
      );
    }

    if (
      !params.resourceTypes.every((type) =>
        Object.values(PersonalizationResourceType).includes(type)
      )
    ) {
      throw new Error(
        `Invalid type in resourceTypes passed to PersonalizationClient.getCFRU: ${params.resourceTypes}`
      );
    }

    if (!params.limit) {
      throw new Error(
        "Missing required parameter for PersonalizationClient.getCFRU: limit"
      );
    }

    if (typeof params.limit !== "number" || params.limit <= 0) {
      throw new Error(
        `Invalid limit passed to PersonalizationClient.getCFRU: ${params.limit}`
      );
    }

    if (
      !params.thumbnailRequired ||
      typeof params.thumbnailRequired !== "boolean"
    ) {
      throw new Error(
        "Missing required parameter for PersonalizationClient.getCFRU: thumbnailRequired"
      );
    }

    return true;
  };

  validateGetRecent = (params: PersonalizationRecentParams): boolean => {
    if (
      !params.resourceTypes ||
      !Array.isArray(params.resourceTypes) ||
      params.resourceTypes.length === 0
    ) {
      throw new Error(
        "Missing required parameter for PersonalizationClient.getRecent: resourceTypes"
      );
    }

    if (
      !params.resourceTypes.every((type) =>
        Object.values(PersonalizationResourceType).includes(type)
      )
    ) {
      throw new Error(
        `Invalid type in resourceTypes passed to PersonalizationClient.getRecent: ${params.resourceTypes}`
      );
    }

    if (!params.limit) {
      throw new Error(
        "Missing required parameter for PersonalizationClient.getRecent: limit"
      );
    }

    if (typeof params.limit !== "number" || params.limit <= 0) {
      throw new Error(
        `Invalid limit passed to PersonalizationClient.getRecent: ${params.limit}`
      );
    }

    if (
      !params.thumbnailRequired ||
      typeof params.thumbnailRequired !== "boolean"
    ) {
      throw new Error(
        "Missing required parameter for PersonalizationClient.getRecent: thumbnailRequired"
      );
    }

    return true;
  };

  validateGetRelated = (params: PersonalizationRelatedParams): boolean => {
    if (
      !params.resourceTypes ||
      !Array.isArray(params.resourceTypes) ||
      params.resourceTypes.length === 0
    ) {
      throw new Error(
        "Missing required parameter for PersonalizationClient.getRelated: resourceTypes"
      );
    }

    if (
      !params.resourceTypes.every((type) =>
        Object.values(PersonalizationResourceType).includes(type)
      )
    ) {
      throw new Error(
        `Invalid type in resourceTypes passed to PersonalizationClient.getRelated: ${params.resourceTypes}`
      );
    }

    if (!params.currentResource || typeof params.currentResource !== "string") {
      throw new Error(
        "Missing required parameter for PersonalizationClient.getRelated: currentResource"
      );
    }

    if (!params.limit) {
      throw new Error(
        "Missing required parameter for PersonalizationClient.getRelated: limit"
      );
    }

    if (typeof params.limit !== "number" || params.limit <= 0) {
      throw new Error(
        `Invalid limit passed to PersonalizationClient.getRelated: ${params.limit}`
      );
    }

    if (
      !params.thumbnailRequired ||
      typeof params.thumbnailRequired !== "boolean"
    ) {
      throw new Error(
        "Missing required parameter for PersonalizationClient.getRelated: thumbnailRequired"
      );
    }

    return true;
  };

  validateGetPopular = (params?: PersonalizationPopularParams): boolean => {
    if (!params) {
      return true;
    }

    if (!params.facets || typeof params.facets !== "string") {
      throw new Error(
        "Missing required parameter for PersonalizationClient.getPopular: facets"
      );
    }

    if (!params.limit) {
      throw new Error(
        "Missing required parameter for PersonalizationClient.getPopular: limit"
      );
    }

    if (typeof params.limit !== "number" || params.limit <= 0) {
      throw new Error(
        `Invalid limit passed to PersonalizationClient.getPopular: ${params.limit}`
      );
    }

    return true;
  };

  validateGetRecommendedPopular = (
    params?: PersonalizationRecommendedPopularParams
  ): boolean => {
    if (!params) {
      return true;
    }

    if (
      !params.resourceTypes ||
      !Array.isArray(params.resourceTypes) ||
      params.resourceTypes.length === 0
    ) {
      throw new Error(
        "Missing required parameter for PersonalizationClient.getRecommendedPopular: resourceTypes"
      );
    }

    if (
      !params.resourceTypes.every((type) =>
        Object.values(PersonalizationResourceType).includes(type)
      )
    ) {
      throw new Error(
        `Invalid type in resourceTypes passed to PersonalizationClient.getRecommendedPopular: ${params.resourceTypes}`
      );
    }

    if (!params.limit) {
      throw new Error(
        "Missing required parameter for PersonalizationClient.getRecommendedPopular: limit"
      );
    }

    if (typeof params.limit !== "number" || params.limit <= 0) {
      throw new Error(
        `Invalid limit passed to PersonalizationClient.getRecommendedPopular: ${params.limit}`
      );
    }

    if (
      !params.thumbnailRequired ||
      typeof params.thumbnailRequired !== "boolean"
    ) {
      throw new Error(
        "Missing required parameter for PersonalizationClient.getRecommendedPopular: thumbnailRequired"
      );
    }

    return true;
  };

  validateGetRecommendedTPR = (
    params: PersonalizationRecommendedTPRParams
  ): boolean => {
    if (!params) {
      throw new Error(
        `Missing required parameters for PersonalizationClient.getRecommendedTPR: resourceTypes, limit`
      );
    }

    if (params.resourceTypes && typeof params.resourceTypes !== "string") {
      throw new Error(
        `Invalid resourceTypes passed to PersonalizationClient.getRecommendedTPR: ${params.resourceTypes}`
      );
    }

    if (params.limit  &&
      typeof params.limit !== "number" ||
      params.limit <= 0 || params.limit >= 1000
    ) {
      throw new Error(
        `Invalid limit passed to PersonalizationClient.getRecommendedTPR: ${params.limit}`
      );
    }

    return true;
  };
}
